import Styles from './Textbox.module.css';
import { ChangeEvent, KeyboardEvent, Ref, useCallback, useMemo, useState } from 'react';

/** File-local constants for Textbox. */
const localConstants = {
  keyConfirm: 'Enter',
  inputTypeText: 'text'
} as const;

/** Props for the Textbox component. */
interface ITextboxDTO {
  /** Render a textarea instead of a single-line input. */
  multiline?: boolean;
  /** Right-to-left text direction. */
  rtl?: boolean;
  /** Input type (e.g. 'text', 'email', 'password'). Only used in controlled mode. Defaults to 'text'. */
  type?: string;
  /** Controlled value — when provided with onChange, renders a standard form input. */
  value?: string;
  /** Change callback for controlled mode. */
  onChange?: (value: string) => void;
  /** Blur callback for controlled mode, fired when the input loses focus (e.g. to normalize/format the value). */
  onBlur?: () => void;
  /** Placeholder text for the input. */
  placeholder?: string;
  /** Disables the input. */
  disabled?: boolean;
  /** Maximum length for the input element (forwarded to the underlying `<input>`/`<textarea>`). */
  maxLength?: number;
  /** Native `autocomplete` attribute (e.g. 'off', 'new-password'). Forwarded to the underlying input/textarea. Defaults to browser default. */
  autoComplete?: string;
  /** Native `name` attribute. Forwarded to the underlying input/textarea. */
  name?: string;
  /** Callback fired when the user presses Enter in controlled mode. */
  onConfirm?: () => void;
  /** CSS class for the outer container (replaces default .container). */
  classNameContainer?: string;
  /** CSS class for the display label (uncontrolled mode only). */
  classNameLabel?: string;
  /** CSS class for the input/textarea element (replaces default .input). */
  classNameText?: string;
  /** Ref for the outer container element. */
  ref?: Ref<HTMLDivElement | null>;
}

/**
 * Renders a Textbox control.
 *
 * - **Controlled mode**: supply `value` and `onChange` props for standard form input behaviour.
 * - **Uncontrolled mode**: click-to-edit inline editing with internal state.
 *
 * Consumer className props replace the corresponding defaults — no merging.
 *
 * @param props - Component props.
 * @returns A styled Textbox element.
 */
export function Textbox(props: ITextboxDTO) {
  const {
    ref,
    multiline,
    rtl,
    type,
    value: controlledValue,
    onChange,
    onBlur,
    placeholder,
    disabled,
    maxLength,
    autoComplete,
    name,
    onConfirm,
    classNameContainer,
    classNameLabel,
    classNameText
  } = props;

  // Controlled mode (The parent owns the state)
  const isControlled = controlledValue !== undefined && onChange !== undefined;

  // Uncontrolled mode state (The Textbox manages its own internal state.)
  const [editingValue, setEditingValue] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  // Enter editing mode (uncontrolled)
  const startEditingValue = useCallback(() => {
    setEditingValue(true);
  }, []);

  // Save the edited value and exit editing mode (uncontrolled)
  const saveValue = useCallback(() => {
    if (!editingValue) return;
    setEditingValue(false);
    setInternalValue(internalValue.trim());
  }, [editingValue, internalValue]);

  // Handle key down to confirm (Enter)
  const handleKeyDown = useCallback((handler: () => void, allowShift: boolean, e: KeyboardEvent) => {
    if (e.key === localConstants.keyConfirm && (allowShift || !e.shiftKey)) {
      e.preventDefault();
      handler();
    }
  }, []);

  // Compute class names
  const containerClass = useMemo(() => (classNameContainer ? `${Styles.container} ${classNameContainer}` : Styles.container), [classNameContainer]);
  const inputClass = useMemo(() => (classNameText ? `${Styles.input} ${classNameText}` : Styles.input), [classNameText]);

  // Compute text direction
  const dir = useMemo(() => (rtl ? 'rtl' : undefined), [rtl]);

  // Handle Enter key in controlled mode
  const handleControlledKeyDown = useMemo(() => {
    if (!onConfirm) return undefined;
    return handleKeyDown.bind(null, onConfirm, !!multiline);
  }, [onConfirm, multiline, handleKeyDown]);

  // Controlled mode — standard form input
  if (isControlled) {
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.target.value);
    return (
      <div ref={ref} className={containerClass}>
        {multiline ? (
          <textarea
            value={controlledValue}
            className={inputClass}
            dir={dir}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            autoComplete={autoComplete}
            name={name}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={handleControlledKeyDown}
          />
        ) : (
          <input
            type={type || localConstants.inputTypeText}
            value={controlledValue}
            className={inputClass}
            dir={dir}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            autoComplete={autoComplete}
            name={name}
            onChange={handleChange}
            onBlur={onBlur}
            onKeyDown={handleControlledKeyDown}
          />
        )}
      </div>
    );
  }

  // Uncontrolled mode — click-to-edit label
  if (!editingValue) {
    return (
      <div ref={ref} className={containerClass} tabIndex={0} onClick={startEditingValue} onKeyDown={handleKeyDown.bind(null, startEditingValue, true)}>
        <div className={classNameLabel} dir={dir}>
          {internalValue}
        </div>
      </div>
    );
  }

  // Uncontrolled mode — editing input
  const handleUncontrolledChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setInternalValue(e.target.value);
  return (
    <div ref={ref} className={containerClass} tabIndex={0} onClick={startEditingValue}>
      {multiline ? (
        <textarea value={internalValue} className={inputClass} dir={dir} autoFocus onChange={handleUncontrolledChange} onBlur={saveValue} onKeyDown={handleKeyDown.bind(null, saveValue, true)} />
      ) : (
        <input
          type={localConstants.inputTypeText}
          value={internalValue}
          className={inputClass}
          dir={dir}
          autoFocus
          onChange={handleUncontrolledChange}
          onBlur={saveValue}
          onKeyDown={handleKeyDown.bind(null, saveValue, false)}
        />
      )}
    </div>
  );
}
